import { numberWithCommas } from '@/utils/numberWithCommas'
import { ColumnDef, SortingState } from '@tanstack/react-table'
import * as React from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UsageCostRequestParams, UsageCostResponseBody } from '../../../hooks'
import { useUsageAndCostLabelsMap } from '../../../hooks/useUsageAndCostLabelsMap/useUsageAndCostLabelsMap'
import {
    flattenUsageCostAugmentedEntry,
    flattenUsageCostEntry,
} from '../../../utils/flattenUsageCost'
import { processTableData } from '../../../utils/processTableData'
import { NestedCheckboxGroupOptionType, UsageAndCostTable } from '../../common'
import { useValidDataSetsOptions } from '../UsageAndCostDataSets/useValidDataSetsOptions'
import { getColumns } from './getColumns'

interface UsageAndCostTableContainerProps {
    data: UsageCostResponseBody
    dataSets: string[]
    filters: UsageCostRequestParams
}

export const UsageAndCostTableContainer: React.FC<
    UsageAndCostTableContainerProps
> = ({ data, dataSets, filters }) => {
    const [sorting, setSorting] = useState<SortingState>([])
    const columns = useUsageAndCostColumns(dataSets, filters)
    const flattenedData = data.items.map((entry) =>
        flattenUsageCostAugmentedEntry(entry)
    )
    const processedData = processTableData(flattenedData)
    const flattenedTotals = flattenUsageCostEntry(data.totals)
    const processedTotals = processTableData([flattenedTotals])[0]
    const summaryCells = useSummaryCells(processedTotals, columns)

    return (
        <UsageAndCostTable
            data={processedData}
            columns={columns}
            sorting={sorting}
            setSorting={setSorting}
            summaryCells={summaryCells}
        />
    )
}

const useUsageAndCostColumns = (
    dataSets: string[],
    filters: UsageCostRequestParams = {} as UsageCostRequestParams
) => {
    const labelsMap = useUsageAndCostLabelsMap(filters, true)
    const validDataSetsOptions = useValidDataSetsOptions(filters)

    const computedDataSets = dataSets.filter((dataSet) => {
        return isDataSetValid(dataSet, validDataSetsOptions)
    }, [])

    computedDataSets.unshift('date')

    const columns = useMemo(
        () => getColumns(labelsMap, computedDataSets),
        [computedDataSets, labelsMap]
    )

    return columns
}

const isDataSetValid: (
    dataSet: string,
    validDataSetsOptions: NestedCheckboxGroupOptionType[]
) => boolean = (dataSet, validDataSetsOptions) => {
    return validDataSetsOptions.some((validDataSetsOption) => {
        const children = validDataSetsOption.children ?? []
        const isValid =
            validDataSetsOption.value === dataSet ||
            isDataSetValid(dataSet, children)
        return isValid
    })
}

/**
 * Generates summary cells for the usage and cost table based on the provided data and columns.
 *
 * Given:
 *
 * ```plain
 *    root (skipped in results)
 *     / \
 *    o   o
 *   / \
 *  o   o
 * ```
 *
 * Navigates the nodes in the following order (DFS, rightmost path), and builds the results array accordingly:
 *
 * ```plain
 *    root (skipped in results)
 *     / \
 *    2   1
 *   / \
 *  4   3
 * ```
 *
 * @param data - An object representing the summary data for the table.
 * @param columns - An array of column definitions used in the table.
 * @returns An array of React nodes representing the summary cells.
 */
const useSummaryCells = (
    data: Record<string, unknown>,
    columns: ColumnDef<unknown>[]
): React.ReactNode[] => {
    const { t } = useTranslation('translations')
    const summaryCells: React.ReactNode[] = []
    const stack = columns.slice()

    while (stack.length > 0) {
        const column = stack.pop()

        if (!column) {
            continue
        }

        if ('columns' in column && column.columns) {
            stack.push(...column.columns.slice())
            continue
        }

        if (!column.id || column.id === 'date') {
            continue
        }

        const value = data[column.id]

        switch (typeof value) {
            case 'number':
                summaryCells.unshift(numberWithCommas(value, 2))
                break
            default:
                summaryCells.unshift(<>{value ?? ''}</>)
        }
    }

    summaryCells.unshift(t('analytics.usage_and_cost.table.summary.total'))

    return summaryCells
}
